import { Fragment, type ReactElement, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { getBreadcrumbItems } from "@/lib/navigation";
import { cn } from "@/lib/utils";

interface AppBreadcrumbsProps {
  className?: string;
}

export function AppBreadcrumbs({
  className,
}: AppBreadcrumbsProps): ReactElement | null {
  const location = useLocation();
  const items = useMemo(
    () => getBreadcrumbItems(location.pathname),
    [location.pathname],
  );

  if (items.length === 0) {
    return null;
  }

  const lastIndex = items.length - 1;

  return (
    <Breadcrumb className={cn("flex-1 min-w-0", className)}>
      <BreadcrumbList>
        {items.map((item, index) => {
          const isLast = index === lastIndex;

          return (
            <Fragment key={item.path}>
              <BreadcrumbItem className="min-w-0 truncate">
                {isLast ? (
                  <BreadcrumbPage className="truncate block">
                    {item.title}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link
                      to={item.path}
                      className="truncate block max-w-[120px] sm:max-w-none"
                    >
                      {item.title}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
